import { Bell, BellRing, CircleHelp, Eye, LogOut, MessageSquare, Palette } from 'lucide-react';
import { Link } from 'react-router';
import { applyPalette } from '../lib/palette';
import { ThemeGrid } from '../ui/ThemePicker';
import { Fragment, useState } from 'react';
import { api } from '../lib/api';
import { BROWSER_CATEGORIES, NOTIFY_LABELS, type NotifyCategory, type NotifyPrefs, type Parent } from '../lib/types';
import { InstallButton } from './InstallButton';
import { syncPushSubscription } from '../lib/push';
import { IntegrationsSettings } from './Integrations';
import { AccountSecurity } from './AccountSecurity';
import { useSupportUnread } from './supportUnread';
import { useParent } from './ParentContext';
import { FamilySwitcher } from './FamilySwitcher';
import { Collapsible, Fold } from '../ui/Collapsible';
import { FamilyFeatures } from './ChildFeatures';
import { FamilySettings } from './Family';
import { GuardianProfile, SchoolSettings } from './FamilyAccount';
import { PlanSection } from './Billing';

export function Settings() {
  const supportUnread = useSupportUnread();
  const { parent, setParent, signOut, toast } = useParent();
  const [perm, setPerm] = useState(typeof Notification === 'undefined' ? 'unsupported' : Notification.permission);

  const prefs: NotifyPrefs = parent.notifyPrefs ?? {
    focus: !!parent.emailAlerts,
    offline: !!parent.emailAlerts,
    done: true,
    help: true,
    chat: false,
    whiteboard: false,
    support: true,
    behind: true,
    deadlines: true,
    evaluator: true,
  };
  const browserPrefs: NotifyPrefs = parent.browserPrefs ?? { focus: true, offline: true, done: true, help: true, chat: true, whiteboard: true, support: true, behind: true, deadlines: true, evaluator: true };
  async function setNotifyPref(category: NotifyCategory, on: boolean) {
    try {
      setParent(await api.patch<Parent>('/api/me', { notifyPrefs: { ...prefs, [category]: on } }));
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }
  async function setBrowserPref(category: NotifyCategory, on: boolean) {
    try {
      setParent(await api.patch<Parent>('/api/me', { browserPrefs: { ...browserPrefs, [category]: on } }));
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }
  // One-line summaries for the notification folds (#601).
  const cats = Object.keys(NOTIFY_LABELS) as NotifyCategory[];
  const emailCount = parent.emailAvailable ? cats.filter((c) => prefs[c]).length : 0;
  const deviceCount = cats.filter((c) => BROWSER_CATEGORIES.includes(c) && browserPrefs[c]).length;
  const matrixSummary = `${parent.emailAvailable ? `${emailCount} by email` : 'No email'} · ${deviceCount} on this device`;
  const deviceSummary = perm === 'granted' ? 'Allowed' : perm === 'unsupported' ? 'Not supported' : perm === 'denied' ? 'Blocked' : 'Not allowed';
  const [testing, setTesting] = useState(false);
  // Fire a test on both channels the parent has available, so they can confirm each one reaches them (#213).
  async function sendTest() {
    setTesting(true);
    let deviceShown = false;
    try {
      if ('Notification' in window) {
        let p = Notification.permission;
        if (p === 'default') p = await Notification.requestPermission();
        setPerm(p);
        if (p === 'granted') {
          new Notification('ClassGuardian', { body: 'Test notification — on-device alerts are working.', icon: '/icon.svg' });
          navigator.vibrate?.(200);
          deviceShown = true;
          void syncPushSubscription(); // make sure closed-app push is registered too
        }
      }
    } catch {
      /* on-device notifications are a bonus */
    }
    // The server handles the email and the closed-app push; it reports what it actually sent.
    let emailed = false;
    let pushed = 0;
    try {
      const r = await api.post<{ emailed: boolean; pushed: number }>('/api/me/test-notification');
      emailed = r.emailed;
      pushed = r.pushed;
    } catch (e) {
      // 503 just means no email + no push subscription yet; only surface a real error if nothing showed.
      if (!deviceShown) toast({ text: (e as Error).message, tone: 'alert' });
    }
    const parts = [];
    if (deviceShown) parts.push('shown on this device');
    if (emailed) parts.push(`emailed to ${parent.email}`);
    if (pushed) parts.push(`pushed to ${pushed} device${pushed === 1 ? '' : 's'}`);
    if (parts.length) toast({ text: `Test notification ${parts.join(', ')}.`, tone: 'good' });
    else if (deviceShown) toast({ text: 'Shown on this device.', tone: 'good' });
    setTesting(false);
  }


  return (
    <div className="page narrow">
      <h1>Settings</h1>
      <FamilySwitcher />
      {/* Switching families (#506) remounts the sections, so each loads the new family's settings. */}
      <Fragment key={parent.activeFamilyId ?? ''}>

        {/* One Settings tab (#496): the family's setup, then yours, then the rest. */}
        {/* The plan first, in its own section (#542). */}
        <PlanSection />

        <h2 className="settings-group">Family</h2>
        <FamilySettings />
        <Collapsible id="features" title="Features for your children" icon={<Eye size={18} />}>
          <FamilyFeatures />
        </Collapsible>
        <SchoolSettings />

        <h2 className="settings-group">You</h2>
        <GuardianProfile />
        <AccountSecurity />

        <Collapsible id="alerts" title="Notifications" icon={<Bell size={18} />}>
          <p className="muted small">
            Choose how each kind of alert reaches you. <strong>Email</strong> is sent by the server; <strong>On this device</strong> pops
            up a notification in your browser (or installed app) when ClassGuardian is in the background.
          </p>

          <WeeklySummaryToggle />

          <Fold title="What you’re told about" summary={matrixSummary}>
            <div className="notify-matrix" role="table" aria-label="Notification preferences">
              <div className="notify-row notify-head" role="row">
                <span role="columnheader">Notify me about</span>
                <span role="columnheader">Email</span>
                <span role="columnheader">On this device</span>
              </div>
              {cats.map((cat) => (
                <div key={cat} className="notify-row" role="row">
                  <span role="cell">{NOTIFY_LABELS[cat]}</span>
                  <span role="cell" className="notify-cell">
                    <input
                      type="checkbox"
                      aria-label={`Email: ${NOTIFY_LABELS[cat]}`}
                      checked={!!prefs[cat]}
                      disabled={!parent.emailAvailable}
                      onChange={(e) => setNotifyPref(cat, e.target.checked)}
                    />
                  </span>
                  <span role="cell" className="notify-cell">
                    {BROWSER_CATEGORIES.includes(cat) && (
                      <input
                        type="checkbox"
                        aria-label={`On this device: ${NOTIFY_LABELS[cat]}`}
                        checked={!!browserPrefs[cat]}
                        onChange={(e) => setBrowserPref(cat, e.target.checked)}
                      />
                    )}
                  </span>
                </div>
              ))}
            </div>

            {!parent.emailAvailable && (
              <p className="muted small">Email isn’t set up on this ClassGuardian server yet, so email notifications can’t be sent.</p>
            )}
          </Fold>

          <Fold title="This device" summary={deviceSummary}>
            <div className="stack notify-device">
              {perm === 'granted' ? (
                <p className="good-text small">On-device notifications are allowed for this browser.</p>
              ) : perm === 'unsupported' ? (
                <p className="muted small">This browser can’t show notifications. On iPhone, add ClassGuardian to the Home Screen first.</p>
              ) : (
                <button
                  className="btn"
                  onClick={() =>
                    Notification.requestPermission().then((p) => {
                      setPerm(p);
                      if (p === 'granted') void syncPushSubscription();
                    })
                  }
                  disabled={perm === 'denied'}
                >
                  {perm === 'denied' ? 'Notifications blocked in browser settings' : 'Allow on-device notifications'}
                </button>
              )}
              <button className="btn" onClick={sendTest} disabled={testing || (perm !== 'granted' && !parent.emailAvailable)}>
                <BellRing size={16} /> {testing ? 'Sending…' : 'Send test notification'}
              </button>
              <InstallButton />
            </div>
          </Fold>
        </Collapsible>

        <AppearanceCard />

        <h2 className="settings-group">More</h2>
        <IntegrationsSettings />

        {/* Help and Feedback are separate entries (#534), as in the account menu. */}
        <Collapsible id="help" title="Help" icon={<CircleHelp size={18} />}>
          <p className="muted small">How to set up a child’s computer, everyday tips, how to call, text or email us, and the full guide to every option.</p>
          <div className="row-gap wrap">
            <Link to="/help" className="btn">
              Open Help
            </Link>
            <Link to="/wiki" className="btn">
              Open the guide
            </Link>
          </div>
        </Collapsible>
        <Collapsible id="feedback" title="Feedback" icon={<MessageSquare size={18} />}>
          <p className="muted small">Ask a question, report a problem or suggest an idea, and follow the replies from ClassGuardian support.</p>
          <div>
            <Link to="/feedback" className="btn">
              Open Feedback
              {supportUnread > 0 && (
                <span className="tag bug">
                  {supportUnread} new {supportUnread === 1 ? 'reply' : 'replies'}
                </span>
              )}
            </Link>
          </div>
        </Collapsible>

        <Collapsible id="privacy" title="Privacy">
          <p className="muted small">
            ClassGuardian only lets you watch during school time, never during breaks, and shows your child when you are watching or
            talking (with Hidden mode on, it doesn’t, so tell them). Live view is never recorded. Website history is kept for{' '}
            {parent.historyDays ?? 90} days, then deleted.
          </p>
        </Collapsible>

        <button className="btn wide" onClick={signOut}>
          <LogOut size={18} /> Sign out
        </button>
      </Fragment>
    </div>
  );
}

function AppearanceCard() {
  const { parent, setParent, toast } = useParent();
  return (
    <Collapsible id="appearance" title="Appearance" icon={<Palette size={18} />}>
      <p className="muted small">
        Pick how ClassGuardian looks: light, dark or matching your device, a color in light or dark, or a gradient. It follows your account to every
        device; each student picks their own in the Student Portal.
      </p>
      <ThemeGrid
        value={parent.theme ?? ''}
        onChange={(t) => {
          applyPalette(t); // right away; saved to the account below
          api
            .patch<Parent>('/api/me', { theme: t })
            .then(setParent)
            .catch((e) => toast({ text: (e as Error).message, tone: 'alert' }));
        }}
      />
    </Collapsible>
  );
}

const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const hourText = (h: number) => new Date(2000, 0, 1, h).toLocaleTimeString(undefined, { hour: 'numeric' });

/** The weekly summary email (#556): on or off for you. Hidden when the server doesn't send it. */
function WeeklySummaryToggle() {
  const { parent, setParent, toast } = useParent();
  const w = parent.weeklySummary;
  if (!w?.available) return null;
  async function set(on: boolean) {
    try {
      setParent(await api.patch<Parent>('/api/me', { weeklySummary: on }));
      toast({ text: on ? 'You’ll get the weekly summary.' : 'Weekly summary turned off.', tone: 'good' });
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }
  return (
    <label className="check weekly-toggle">
      <input type="checkbox" checked={w.on} onChange={(e) => set(e.target.checked)} />
      <span>
        <strong>Weekly summary email</strong>
        <span className="muted small block">
          Every {WEEKDAY_NAMES[w.day]} at {hourText(w.hour)}: each child’s school time, attendance, assignments, grades and reading for the week, and what needs
          attention.
        </span>
      </span>
    </label>
  );
}

